import React, { useContext, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import { Typography } from 'foundations-library/components'
import { ScreenSizeContext } from 'providers'
import Coupon from '../../../../components/Coupon'
import Picture from 'picture'
import Button from '../../../../components/Button'
import Feature from '../../../../components/Feature'
import { useAppContext } from '../../../../contexts/AppContext'
import { processLink, modalCondition, urlCondition, integrationCondition, handleGTM, GTM_APP_DETAILS_NAME } from '../../../../utils/utils'
import { useHistory } from 'react-router-dom'
import { ZohoUserProvider, ZohoUsersProvider } from 'providers'
import ZohoUserModal from 'zoho-user-modal'
import { getValue } from 'remote-config-value'
import { AppHeader, Button as ButtonInterface } from '../../../../utils/objects'

const isSmallScreen = (screenSize) => screenSize.mobile || screenSize.tablet

const jss = (theme) => {
    const logoSize = '160px'
    const column = {
        flex: ({ screenSize }) => isSmallScreen(screenSize) ? '100%' : '50%',
        maxWidth: ({ screenSize }) => isSmallScreen(screenSize) ? '100%' : '50%'
    }
    return {
        content: {
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: ({ screenSize }) => isSmallScreen(screenSize) && 'column',
            background: ({ backgroundColor }: { backgroundColor?: string }) => backgroundColor || 'linear-gradient(100.59deg, #F2F1F1 13.72%, #FD9A27 106.72%)'
        },
        textContainer: {
            ...column,
            padding: ({ screenSize }) => isSmallScreen(screenSize) ? '20px' : '40px 0 145px 70px',
            display: 'flex',
            flexDirection: ({ screenSize }) => isSmallScreen(screenSize) && 'column',
            gap: '30px',
            textAlign: ({ screenSize }) => isSmallScreen(screenSize) && 'center'
        },
        text: {
            paddingTop: '18px'
        },
        titleContainer: {
            display: 'flex',
            justifyContent: ({ screenSize }) => isSmallScreen(screenSize) && 'center',
            alignItems: 'center',
            gap: '10px',
            '& .coupon': {
                boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)'
            }
        },
        logo: {
            margin: ({ screenSize }) => isSmallScreen(screenSize) && 'auto',
            flexShrink: 0,
            width: logoSize,
            '& img': {
                width: '100%'
            }
        },
        buttonsContainer: {
            display: 'flex',
            justifyContent: ({ screenSize }) => isSmallScreen(screenSize) ? 'center' : 'flex-start',
            columnGap: '20px',
            marginBottom: '94px'
        },
        featuresContainer: {
            display: 'flex',
            flexDirection: ({ screenSize }) => isSmallScreen(screenSize) && 'column',
            columnGap: ({ screenSize }) => !isSmallScreen(screenSize) && '20px',
            marginTop: ({ screenSize }) => !isSmallScreen(screenSize) && '50px',
            rowGap: ({ screenSize }) => isSmallScreen(screenSize) && '18px'
        },
        featuresColumn: {
            display: 'flex',
            flexDirection: 'column',
            rowGap: '18px',
            alignItems: ({ screenSize }) => isSmallScreen(screenSize) && 'center'
        },
        imgContainer: {
            ...column,
            alignSelf: ({ screenSize }) => !isSmallScreen(screenSize) && 'flex-end',
            padding: ({ screenSize }) => isSmallScreen(screenSize) ? '20px 20px 0' : '40px 40px 0 60px',
            '& img': {
                width: '100%',
                objectFit: 'cover',
                display: 'block',
                margin: 'auto'
            }
        },
        company: {
            marginTop: '6px'
        },
        phrase: {
            whiteSpace: ({ screenSize }) => !isSmallScreen(screenSize) && 'nowrap',
            marginTop: '6px',
            marginBottom: '15px'
        }
    }
}

const useStyles = makeStyles(jss)

/**
 *
 */
const Index = (): JSX.Element => {
    const { app, phoneCom } = useAppContext()
    const screenSize = useContext(ScreenSizeContext)
    const [openModal, setOpenModal] = useState(false)
    const [manageUsers, setManageUsers] = useState(false)
    const { title, tags, features, image, backgroundColor } = app.details.header as AppHeader
    const styles = useStyles({ screenSize, backgroundColor })
    const history = useHistory()
    const userRole = phoneCom?.role
    const isAdmin = userRole === 'account'

    const handleCloseModal = () => setOpenModal(false)

    const renderModalComponent = () => {
        if (!userRole) return <></>

        const userModal = (
            <ZohoUserProvider>
                {openModal && <ZohoUserModal open={openModal} handleClose={handleCloseModal} manageUsers={manageUsers} app={app} />}
            </ZohoUserProvider>
        )

        return (
            isAdmin
                ? <ZohoUsersProvider>
                    {userModal}
                </ZohoUsersProvider>
                : userModal
        )
    }

    const handleBtnClick = (button: ButtonInterface) => {
        const { action: { type, modal, url }, text } = button
        const appTitle = app.title.toLowerCase().replace(/\s+/g, '_')
        const btn = text.toLowerCase().replace(/\s+/g, '_')

        switch (true) {
                case modalCondition(type): {
                    const isManageUserAccessModal = modal?.toLowerCase() === 'manage-user-access'
                    setManageUsers(isManageUserAccessModal)
                    setOpenModal(true)
                    break
                }
                case urlCondition(type): {
                    if (url) processLink(url, history)

                    break
                }
                case integrationCondition(type): {
                    if (app.identifier?.toLowerCase() === getValue('esim_app_identifier')?.toLowerCase()) processLink('/stepper', history, true)

                    break
                }
                default:
                    break
        }

        handleGTM({
            action: GTM_APP_DETAILS_NAME,
            label: `clicked-${btn}`,
            event: `PDC_${appTitle}_clicked_${btn}`,
            other: {
                items: [{ item_name: app.title }]
            }
        })
    }

    const renderFeatures = (side: 'left' | 'right') => {
        if (!(Array.isArray(features) && features.length)) return null

        return features.map((feature, index) => {
            if (index % 2 === (side === 'left' ? 0 : 1)) {
                return <Feature key={feature.name} name={feature.name} icon={feature.icon} link={feature.link} />
            }
            return null
        })
    }

    const doesAnyButtonOpensModal = app.buttons?.some(button => button.action.type.toLowerCase() === 'modal')

    return (
        <div className={styles.content}>
            <div className={styles.textContainer}>
                <Picture sources={[{ src: app.icon }]} thumbnail={app.inlineIcon} alt={`${app.title} icon`} attributes={{ className: styles.logo }} />
                <div className={styles.text}>
                    <div className={styles.titleContainer}>
                        <Typography variant='h3' color='neutral-900' data-testid={`${app.title}-title`}>{app.title}</Typography>
                        {Array.isArray(tags) && tags.length && <Coupon title={tags[0].text} icon={tags[0].icon} color={tags[0].color} />}
                    </div>
                    <Typography className={styles.company} variant='body1' color='neutral-500' data-testid='company'>{app.company}</Typography>
                    <Typography variant='h8' className={styles.phrase} color='neutral-900' data-testid='phrase' dangerouslySetInnerHTML={{ __html: title }} /> {/* eslint-disable-line @typescript-eslint/naming-convention */}
                    <div className={styles.buttonsContainer} data-testid='buttons'>
                        {
                            app.buttons &&
                            app.buttons.map(
                                (button, index) => (
                                    <Button
                                        key={index}
                                        handleClick={handleBtnClick.bind(this, button)}
                                        {...button}
                                    />
                                )
                            )
                        }
                    </div>
                    <div className={styles.featuresContainer} data-testid='features'>
                        <div className={styles.featuresColumn}>
                            {renderFeatures('left')}
                        </div>
                        <div className={styles.featuresColumn}>
                            {renderFeatures('right')}
                        </div>
                    </div>
                    {doesAnyButtonOpensModal && renderModalComponent()}
                </div>
            </div>
            <div className={styles.imgContainer}>
                {image && <Picture sources={image?.sources || []} thumbnail={image?.thumbnail} alt={`${app.title} header image`} attributes={{ className: styles.leftImg }} />}
            </div>
        </div>
    )
}

export default Index
